<template>
  <Modal v-model="isModalOpen" class="productdetail-view productmodal">
    <!-- No product (yet) -->
    <div v-if="!product" class="row">
      <!-- 404 -->
      <div v-if="is404" class="col-12">
        <div class="loader">Product not found</div>
      </div>

      <!-- Loading state -->
      <div v-else class="col-12">
        <div class="loader">
          <Loading />
        </div>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-12 col-md-4 position-relative">
        <div class="imagesfield">
          <!-- Main image -->
          <div class="image-ratio-3-4">
            <img
              class="grote-foto"
              :src="images[activePhotoIndex]"
              :title="helptext"
              :alt="`${cleanQuotes(product.title)} from ${
                product.store.store_name
              }`"
            />
          </div>

          <!-- Gallery -->
          <div class="photo-gallery small">
            <div
              v-for="(img, index) in images"
              :key="img"
              :class="{ photo: true, active: activePhotoIndex === index }"
              @click="activePhotoIndex = index"
            >
              <div class="image-ratio-1-1">
                <img :src="img" :title="helptext" :alt="helptext" />
              </div>
            </div>
          </div>
        </div>
        <img
          v-if="product.giftcard === 'true'"
          src="/static/img/giftcard/giftcard-icon.png"
          title="Dit product is te koop met de Fair Fashion Giftcard"
          alt="Dit product is te koop met de Fair Fashion Giftcard"
          class="img-fluid promo-icon"
        />
      </div>
      <div class="col-12 col-md-8">
        <div class="row">
          <div class="col-12">
            <!-- Product title -->
            <h2 class="h1 mb-3">
              {{ cleanQuotes(product.title) }}
            </h2>

            <!-- Favourite button -->
            <Favorite
              tag="button"
              class="btn favorite-heart"
              :product-id="product.productID"
              :store-name="product.store.store_name"
              :product-title="product.title"
              :show-icon="true"
              icon-class="orange"
            />
          </div>

          <div class="col-12 mb-3">
            <div class="pricebox d-flex align-items-end">
              <!-- Price and shipping fee -->
              <h3 v-if="product.old_price != 'None'" class="price text-danger">
                <del> {{ currencySymbol }} {{ product.old_price }} </del>
              </h3>
              <h3
                :class="`price currentprice ${
                  product.old_price != 'None' ? 'float-end' : ''
                }`"
              >
                {{ currencySymbol }}
                {{ product.price }}
                {{ product.converted === 'true' ? '*' : '' }}
              </h3>
            </div>
            <div v-if="product.converted === 'true'">
              <p id="converted-msg" class="mb-0">
                {{ $i18n.productModal.convertedPrice }}
              </p>
            </div>
          </div>

          <div class="col-12 mb-4">
            <div class="row align-items-center">
              <div class="col-12 col-md-9">
                <!-- Available sizes of the item -->
                <div class="d-flex flex-direction-row align-items-center">
                  <p class="labels pe-2 m-0 flex-shrink-0">
                    {{ $i18n.productModal.sizes }}:
                  </p>
                  <div id="sizes" class="product-sizes">
                    <div
                      v-for="size in product.sizes"
                      :key="size"
                      class="size-box"
                    >
                      {{ size }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <a
              class="nostyle productlink"
              :href="product.redirect_url"
              :target="'new-tab-' + product.productID"
              @click="onOutlinkClick"
            >
              <!-- Order button -->
              <button
                id="order-store"
                class="cece-button-primary d-flex align-items-center w-100 mb-2"
              >
                <span class="flex-grow-1">
                  {{ $i18n.productModal.orderAt }}
                  <span id="order-store-text">
                    {{ product.store.store_name }}
                  </span>
                </span>
                <IconChevronRight class="white" />
              </button>
            </a>
            <a class="nostyle productpagelink text-decoration-none" href="">
              <Favorite
                :tag="button"
                :product-id="product.productID"
                :store-name="product.store.store_name"
                :product-title="product.title"
                :show-icon="true"
                icon-class="orange me-2"
                class="favorite-button btn btn-link d-flex justify-content-center align-items-center w-100 text-primary mb-2"
              >
                <IconClose class="orange me-2" />
                <span class="add-to-favorites">
                  {{ $i18n.productModal.saveFavorite }}
                </span>
                <span class="remove-from-favorites">
                  {{ $i18n.productModal.removeFavorite }}
                </span>
              </Favorite>
            </a>
          </div>

          <div class="col-12 mb-3 mb-md-4">
            <div class="brand-labels-and-certificates">
              <div class="row">
                <!-- Logo -->
                <div
                  :class="`col-12 mb-3 ${
                    !brandHasCertificates
                      ? 'col-md-6 d-flex justify-content-center align-items-center'
                      : ''
                  }`"
                >
                  <a class="store-logo d-inline-block" href="#">
                    <img class="mw-100" :src="product.store.logo" />
                  </a>
                </div>

                <!-- Labels -->
                <div
                  :class="`col-12 mt-md-n1 ${
                    !brandHasCertificates ? 'col-md-6' : 'col-md-5'
                  }`"
                >
                  <div class="d-flex">
                    <CollapsableIconList
                      :title="$i18n.productModal.labelsTitle"
                      :title-tooltip="$i18n.productModal.labelsTooltip"
                      :title-tooltip-read-more-link="
                        $i18n.productModal.criteriaLink
                      "
                      :items="labelIcons"
                    />
                  </div>
                </div>

                <!-- Certificates -->
                <div
                  v-if="brandHasCertificates"
                  class="col-12 col-md-7 mt-md-n1"
                >
                  <CollapsableIconList
                    :title="$i18n.productModal.certificatesTitle"
                    :items="certificateIcons"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Product info tabs -->
          <div class="col-12 product-info">
            <!-- Short overview -->
            <h3 class="h6 m-0">
              <a
                class="product-info-collapse-button"
                href="#product-short-overview-collapse"
                data-bs-toggle="collapse"
                role="button"
                aria-expanded="true"
                aria-controls="product-short-overview-collapse"
              >
                {{ $i18n.productModal.shortDescription }} <IconChevronUp />
                <IconChevronDown />
              </a>
            </h3>
            <div id="product-short-overview-collapse" class="collapse show">
              <!-- eslint-disable vue/no-v-html -->
              <div
                id="shorttext"
                class="short-version"
                v-html="cleanQuotes(product.text)"
              />

              <!-- Source -->
              <p class="text-end">
                <i>
                  {{ $i18n.productModal.source }}
                  <a
                    :target="'new-tab-' + product.productID"
                    :href="product.redirect_url"
                    class="store-url"
                  >
                    {{ product.store.store_name }}
                  </a>
                </i>
              </p>

              <!-- Product tags -->
              <div
                v-if="
                  product.sustainability_tags_by_category &&
                  product.sustainability_tags_by_category.length > 0
                "
                class="product-tags-section"
              >
                <table>
                  <tr
                    v-for="category in product.sustainability_tags_by_category"
                    :key="category.name"
                  >
                    <th>
                      <img :src="`/static/img/emoji/${category.icon}.svg`" />
                      {{ category.name }}
                    </th>
                    <td>
                      <ul>
                        <li
                          v-for="tag in category.tags"
                          :key="category.name + tag.tag"
                        >
                          {{ tag.tag }}
                        </li>
                      </ul>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <!-- Product details -->
            <h3 class="h6 m-0">
              <a
                class="product-info-collapse-button collapsed"
                href="#product-details-collapse"
                data-bs-toggle="collapse"
                role="button"
                aria-expanded="false"
                aria-controls="product-details-collapse"
              >
                {{ $i18n.productModal.details }} <IconChevronUp />
                <IconChevronDown />
              </a>
            </h3>
            <div id="product-details-collapse" class="collapse">
              <!-- eslint-disable vue/no-v-html -->
              <p
                id="extratext"
                v-html="cleanQuotes(product.extra_text + product.promo_field)"
              ></p>
              <!-- eslint-enable vue/no-v-html -->
            </div>
            <!-- About brand and shop -->
            <h3 class="h6 m-0">
              <a
                class="product-info-collapse-button collapsed"
                href="#about-brand-and-shop-collapse"
                data-bs-toggle="collapse"
                role="button"
                aria-expanded="false"
                aria-controls="about-brand-and-shop-collapse"
              >
                {{ $i18n.productModal.aboutStoreBrand }}
                <IconChevronUp />
                <IconChevronDown />
              </a>
            </h3>
            <div id="about-brand-and-shop-collapse" class="collapse">
              <!-- Brand text -->
              <!-- eslint-disable vue/no-v-html -->
              <div
                id="brandtext"
                v-html="cleanQuotes(product.brand.about_brand)"
              />
              <!-- eslint-enable vue/no-v-html -->
              <!-- Brand tags -->
              <p
                v-if="product.brand.tags && product.brand.tags.length > 0"
                id="brandtags"
                class="lead mt-n1 mb-4"
              >
                <span
                  v-for="tag in product.brand.tags"
                  :key="tag"
                  class="badge bg-success cece-tooltip me-1"
                  :data-tooltip="tag.display_name + ': ' + tag.description"
                >
                  {{ '#' + tag.tag }}
                </span>
              </p>

              <!-- Store text -->
              <!-- eslint-disable vue/no-v-html -->
              <div id="storetext" v-html="product.store.about" />
              <!-- eslint-enable vue/no-v-html -->
              <!-- Payment methods -->
              <div class="container-pay-methods">
                <p class="shipping-costs" style="position: relative">
                  <strong> {{ $i18n.productModal.paymentMethods }}:</strong>
                </p>
                <img
                  v-for="(name, index) in product.store.pay_method_names"
                  :key="name"
                  :src="product.store.pay_method_icon_urls[index]"
                  :title="name"
                  :alt="name"
                  class="pay-icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from './Modal.vue';
import Favorite from './Favorite.vue';
import CollapsableIconList from './CollapsableIconList.vue';

import IconClose from '../../../../templates/icons/close.svg';
import IconChevronUp from '../../../../templates/icons/chevron-up.svg';
import IconChevronDown from '../../../../templates/icons/chevron-down.svg';
import IconChevronRight from '../../../../templates/icons/chevron-right.svg';
import Loading from '../../../../templates/icons/loading.svg';

export default {
  components: {
    Modal,
    Favorite,
    CollapsableIconList,
    IconClose,
    IconChevronUp,
    IconChevronDown,
    IconChevronRight,
    Loading,
  },
  data() {
    return {
      product: null,
      isModalOpen: false,
      is404: false,
      activePhotoIndex: 0,
    };
  },
  computed: {
    helptext() {
      return this.product
        ? `${this.product.title} from ${this.product.store.store_name}`
        : '';
    },
    currencySymbol() {
      return window.cece.currencySymbol;
    },
    images() {
      if (!this.product) return [];
      return [this.product.primary_image, ...this.product.extra_images];
    },
    brandHasCertificates() {
      return (
        this.product &&
        this.product.brand &&
        this.product.brand.certificates &&
        Object.keys(this.product.brand.certificates).length > 0
      );
    },
    certificateIcons() {
      if (!this.brandHasCertificates) {
        return [];
      }
      const { certificates } = this.product.brand;
      return Object.keys(certificates).map(abbr => ({
        icon: certificates[abbr].img,
        text: certificates[abbr].title,
        link: `/certificates/${abbr}/`,
      }));
    },
    labelIcons() {
      if (
        !this.product ||
        !this.product.brand ||
        !this.product.brand.label_names ||
        this.product.brand.label_names.length === 0
      ) {
        return [];
      }
      return this.product.brand.label_names.map((label, index) => ({
        icon: this.product.brand.label_icon_urls[index],
        text: label,
      }));
    },
  },
  methods: {
    setProduct(product) {
      this.product = product;
    },
    setNotFound() {
      this.is404 = true;
    },
    reset() {
      this.is404 = false;
      this.product = null;
      this.activePhotoIndex = 0;
    },
    openModal() {
      this.isModalOpen = true;
    },
    isOpen() {
      return this.isModalOpen;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    cleanQuotes(text = '') {
      return text.replace(/\\'/g, "'");
    },
    onOutlinkClick() {
      _paq.push(['trackLink', this.product.link, 'link']);
      _paq.push([
        'trackEvent',
        'ProductOutlink',
        encodeURIComponent(document.URL),
        this.product.store.store_name,
        this.product.link,
      ]);
    },
  },
};

/*
 */
</script>
